import React, { useState } from "react";
import { motion } from "framer-motion";
import { 
  FaUser, 
  FaPhone, 
  FaBuilding, 
  FaMapMarkerAlt, 
  FaMapPin, 
  FaRoad, 
  FaEnvelope, 
  FaPaperPlane,
  FaClock,
  FaExternalLinkAlt,
  FaCommentAlt
} from "react-icons/fa";
import { db } from '../../config/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import DynamicGoogleMap from '../../components/DynamicGoogleMap';

interface ContactDetail {
  type: 'phone' | 'email';
  value: string;
  display: string;
}

interface ContactInfoItem {
  title: string;
  icon: React.ReactNode;
  details: ContactDetail[];
}

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    message: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error' | null; message: string }>({
    type: null,
    message: ''
  });

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.mobile.trim()) {
      newErrors.mobile = 'Mobile number is required';
    } else if (!/^\d{10}$/.test(formData.mobile)) {
      newErrors.mobile = 'Please enter a valid 10-digit mobile number';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setStatus({ type: null, message: '' });

    try {
      // Add document to contacts collection
      const contactsRef = collection(db, 'contacts');
      await addDoc(contactsRef, {
        ...formData,
        timestamp: serverTimestamp()
      });
      
      setStatus({
        type: 'success',
        message: 'Thank you for your message. We will get back to you soon!'
      });
      setFormData({ name: '', email: '', mobile: '', message: '' });
    } catch (error) {
      console.error('Error saving message:', error);
      setStatus({
        type: 'error',
        message: 'Sorry, there was an error sending your message. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ContactDetail {
    type: 'phone' | 'email';
    value: string;
    display: string;
    url?: string;
  }

  const contactInfo: ContactInfoItem[] = [
    {
      title: "Phone Numbers",
      icon: <FaPhone className="text-2xl" />,
      details: [
        { 
          type: 'phone',
          value: "+919887710260",
          display: "+91 98877 10260"
        },
        { 
          type: 'phone',
          value: "+919520266015",
          display: "+91 95202 66015"
        },
        { 
          type: 'phone',
          value: "+918219193005",
          display: "+91 82191 93005"
        }
      ]
    },
    {
      title: "Email",
      icon: <FaEnvelope className="text-2xl" />,
      details: [
        {
          type: 'email',
          value: "info@sssloarenergy.in",
          display: "info@sssloarenergy.in"
        }
      ]
    }
  ];

  interface AddressItem {
    icon: 'location' | 'road' | 'pin';
    text: string;
  }

  interface ContactItem {
    icon: 'phone' | 'envelope';
    text: string;
  }

  interface Office {
    title: string;
    address: (string | AddressItem)[];
    contact?: ContactItem[];
  }

  const mainOffices: Office[] = [
    {
      title: "Corporate Office",
      address: [
        { icon: 'location' as const, text: "537, Aditya High Street" },
        { icon: 'road' as const, text: "NH-9, Near Lal Kuan" },
        { icon: 'pin' as const, text: "Ghaziabad (U.P.) - 201001" }
      ],
      contact: [
        { icon: 'phone' as const, text: "+91 9887710260" },
        { icon: 'envelope' as const, text: "info@sssloarenergy.in" }
      ]
    },
    {
      title: "Registered Office",
      address: [
        { icon: 'location' as const, text: "C-97, Opposite ABCD Park" },
        { icon: 'road' as const, text: "Shivalik Nagar" },
        { icon: 'pin' as const, text: "Haridwar (U.K.) - 249403" }
      ],
      contact: [
        { icon: 'phone' as const, text: "+91 9887710260" },
        { icon: 'envelope' as const, text: "info@sssloarenergy.in" }
      ]
    }
  ];

  const officeHours = [
    { day: 'Office Hours - (24x7)', hours: '9:00 AM - 6:00 PM' }
  ] as const;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white relative">
      {/* Background Blur Effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-gray-900/20 to-purple-900/20 backdrop-blur-[100px] pointer-events-none"></div>
      
      {/* Content Container */}
      <div className="relative z-10">
        {/* Header Section */}
        <div className="relative h-[300px] overflow-hidden -mt-[150px]">
          {/* Background with gradient overlay */}
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-b from-gray-900/50 to-gray-900"></div>
            <div className="absolute inset-0 bg-[radial-gradient(circle_800px_at_50%_-100%,#3b82f6,transparent)] opacity-20"></div>
          </div>

          {/* Header Content */}
          <div className="relative w-full h-full z-10 pt-[70px]">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-full">
              <div className="flex flex-col justify-center h-full text-center py-12 sm:py-16">
                {/* Content */}
                <div className="relative mx-auto max-w-3xl">
                  <div className="absolute -left-4 -top-4 w-72 h-72 bg-gradient-to-br from-blue-600/20 to-transparent opacity-50 rounded-full blur-2xl animate-pulse hidden sm:block" />
                  <div className="relative space-y-4 sm:space-y-6">
                    <div className="inline-flex items-center space-x-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-blue-500/10 border border-blue-500/20 backdrop-blur-sm">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
                      </span>
                      <span className="text-xs sm:text-sm text-blue-200 font-medium">Get in Touch</span>
                    </div>
                    
                    <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-white bg-clip-text animate-fade-in-up relative">
                      Contact Us
                      <span className="block mt-2 text-2xl sm:text-4xl text-blue-100/90">We're Here to Help</span>
                      <div className="absolute -right-4 bottom-0 w-24 h-24 bg-gradient-to-tr from-blue-500 to-transparent opacity-20 rounded-full blur-xl animate-pulse delay-200 hidden sm:block" />
                    </h1>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Floating shapes - Only show on larger screens */}
          <div className="absolute inset-0 overflow-hidden hidden sm:block">
            <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl animate-blob" />
            <div className="absolute top-1/3 right-1/4 w-72 h-72 bg-indigo-500/10 rounded-full blur-3xl animate-blob animation-delay-2000" />
            <div className="absolute bottom-1/4 left-1/3 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl animate-blob animation-delay-4000" />
          </div>
        </div>

        {/* Main Content */}
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          {/* Section 1: Contact Form & Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-12 sm:mb-20"
          >
            <div className="text-center mb-10 sm:mb-16">
              <p className="mt-3 text-lg text-gray-300 max-w-2xl mx-auto">Have questions or ready to get started? Our team is here to assist you.</p>
            </div>
            
            <div className="grid lg:grid-cols-2 gap-8 sm:gap-10">
              {/* Contact Form */}
              <motion.form
                onSubmit={handleSubmit}
                className="space-y-5 bg-gray-900/50 backdrop-blur-sm rounded-2xl p-6 sm:p-8 border border-white/5 shadow-xl shadow-blue-500/5"
              >
                <div className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                      Full Name *
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FaUser className="h-4 w-4 text-blue-400" />
                      </div>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className={`block w-full pl-10 pr-4 py-3 rounded-xl bg-gray-800/50 border ${
                          errors.name ? 'border-red-500' : 'border-gray-700/50 hover:border-blue-500/50'
                        } text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all duration-200`}
                        placeholder="John Doe"
                      />
                    </div>
                    {errors.name && (
                      <p className="mt-1.5 text-sm text-red-400">{errors.name}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                        Email Address *
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <FaEnvelope className="h-4 w-4 text-blue-400" />
                        </div>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className={`block w-full pl-10 pr-4 py-3 rounded-xl bg-gray-800/50 border ${
                            errors.email ? 'border-red-500' : 'border-gray-700/50 hover:border-blue-500/50'
                          } text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all duration-200`}
                          placeholder="your@email.com"
                          required
                        />
                      </div>
                      {errors.email && (
                        <p className="mt-1.5 text-sm text-red-400">{errors.email}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="mobile" className="block text-sm font-medium text-gray-300 mb-2">
                        Mobile Number *
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <FaPhone className="h-4 w-4 text-blue-400" />
                        </div>
                        <input
                          type="tel"
                          id="mobile"
                          name="mobile"
                          value={formData.mobile}
                          onChange={handleInputChange}
                          className={`block w-full pl-10 pr-4 py-3 rounded-xl bg-gray-800/50 border ${
                            errors.mobile ? 'border-red-500' : 'border-gray-700/50 hover:border-blue-500/50'
                          } text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all duration-200`}
                          placeholder="9876543210"
                          maxLength={10}
                        />
                      </div>
                      {errors.mobile && (
                        <p className="mt-1.5 text-sm text-red-400">{errors.mobile}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                      Message
                    </label>
                    <div className="relative">
                      <div className="absolute top-3 left-3">
                        <FaCommentAlt className="h-4 w-4 text-blue-400" />
                      </div>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        rows={4}
                        className={`block w-full pl-10 pr-4 py-3 rounded-xl bg-gray-800/50 border ${
                          errors.message ? 'border-red-500' : 'border-gray-700/50 hover:border-blue-500/50'
                        } text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all duration-200`}
                        placeholder="How can we help you?"
                      />
                    </div>
                    {errors.message && (
                      <p className="mt-1.5 text-sm text-red-400">{errors.message}</p>
                    )}
                  </div>

                  <div className="pt-1">
                    <button
                      type="submit"
                      disabled={loading}
                      className={`w-full flex justify-center items-center px-6 py-3.5 border border-transparent rounded-xl text-base font-medium text-white ${
                        loading
                          ? 'bg-blue-500/80 cursor-not-allowed'
                          : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500/50'
                      } transition-all duration-200 shadow-lg hover:shadow-xl hover:-translate-y-0.5`}
                    >
                      {loading ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Sending...
                        </>
                      ) : (
                        <>
                          <FaPaperPlane className="mr-2.5" />
                          Send Message
                        </>
                      )}
                    </button>
                  </div>

                  {status.type && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`p-4 rounded-md ${
                        status.type === 'success' ? 'bg-green-500/20 text-green-200' : 'bg-red-500/20 text-red-200'
                      }`}
                    >
                      {status.message}
                    </motion.div>
                  )}
                </div>
              </motion.form>

              {/* Contact Information */}
              <div className="space-y-6 sm:space-y-8">
                <div className="bg-gradient-to-br from-gray-900/80 to-gray-800/80 backdrop-blur-sm rounded-2xl p-8 border border-white/5 shadow-xl overflow-hidden relative">
                  <div className="absolute -right-10 -top-10 w-40 h-40 bg-blue-500/5 rounded-full blur-2xl"></div>
                  <h3 className="text-2xl font-bold text-white mb-7 relative z-10">Contact Information</h3>
                  <div className="space-y-6 relative z-10">
                    {/* Phone Numbers */}
                    <div className="space-y-4">
                      <div className="flex items-start group">
                        <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-xl bg-gradient-to-br from-blue-600/20 to-blue-700/20 text-blue-400 border border-blue-500/20 group-hover:from-blue-600/30 group-hover:to-blue-700/30 transition-all duration-300">
                          <FaPhone className="text-xl" />
                        </div>
                        <div className="ml-4 pt-1">
                          <p className="text-sm font-medium text-gray-400 mb-2">Phone Numbers</p>
                          <div className="space-y-2">
                            {contactInfo.find(i => i.title === 'Phone Numbers')?.details.map((detail, i) => (
                              <a
                                key={i}
                                href={`tel:${detail.value}`}
                                className="block text-base font-medium text-white hover:text-blue-400 transition-colors duration-300 group/item"
                              >
                                <div className="flex items-center">
                                  <span className="group-hover/item:underline">{detail.display}</span>
                                  <FaExternalLinkAlt className="ml-2 opacity-0 group-hover/item:opacity-100 transition-opacity duration-200" size={12} />
                                </div>
                              </a>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Email */}
                    <div className="flex items-start group">
                      <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-xl bg-gradient-to-br from-blue-600/20 to-blue-700/20 text-blue-400 border border-blue-500/20 group-hover:from-blue-600/30 group-hover:to-blue-700/30 transition-all duration-300">
                        <FaEnvelope className="text-xl" />
                      </div>
                      <div className="ml-4 pt-1">
                        <p className="text-sm font-medium text-gray-400 mb-0.5">Email</p>
                        <a
                          href={`mailto:${contactInfo.find(i => i.title === 'Email')?.details[0]?.value || 'info@sssloarenergy.in'}`}
                          className="text-base font-medium text-white hover:text-blue-400 transition-colors duration-300 flex items-center group/email"
                        >
                          <span className="group-hover/email:underline">
                            {contactInfo.find(i => i.title === 'Email')?.details[0]?.display || 'info@sssloarenergy.in'}
                          </span>
                          <FaExternalLinkAlt className="ml-2 opacity-0 group-hover/email:opacity-100 transition-opacity duration-200" size={12} />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Office Hours */}
                <div className="bg-gradient-to-br from-gray-900/80 to-gray-800/80 backdrop-blur-sm rounded-2xl p-8 border border-white/5 shadow-xl overflow-hidden relative">
                  <div className="absolute -left-10 -bottom-10 w-40 h-40 bg-indigo-500/5 rounded-full blur-2xl"></div>
                  <h3 className="text-2xl font-bold text-white mb-7 relative z-10">Office Hours</h3>
                  <div className="space-y-4 relative z-10">
                    {officeHours.map((hours, index) => (
                      <div key={index} className="flex justify-between items-center py-2.5 px-3.5 bg-gray-800/30 hover:bg-gray-700/50 rounded-xl transition-colors duration-200">
                        <span className="text-sm font-medium text-gray-300 flex items-center">
                          <FaClock className="mr-2.5 text-blue-400" />
                          {hours.day}
                        </span>
                        <span className="text-sm font-medium text-white bg-gray-700/50 px-3 py-1 rounded-lg">
                          {hours.hours}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Section 2: Main Offices */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12"
          >
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-cyan-300 mb-3">
                Our Offices
              </h2>
              <div className="w-20 h-1 bg-gradient-to-r from-blue-500 to-cyan-400 mx-auto rounded-full" />
              <p className="mt-4 text-gray-300 max-w-2xl mx-auto">
                Visit us at our corporate office or get in touch with our team for any inquiries.
              </p>
            </div>
            
            {/* Main Offices */}
            <div className="grid gap-8">
              {mainOffices.map((office, index) => (
                <motion.div
                  key={office.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.15 }}
                  className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl p-6 sm:p-8 shadow-2xl border border-gray-700/50 overflow-hidden relative group"
                  whileHover={{ y: -5, transition: { duration: 0.2 } }}
                >
                  <div className="relative z-10">
                    <div className="flex flex-col sm:flex-row items-start gap-6">
                      <div className="bg-gradient-to-br from-blue-600 to-cyan-500 p-4 rounded-xl shadow-lg flex-shrink-0">
                        <FaBuilding className="text-2xl sm:text-3xl text-white" />
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-4">
                          <h3 className="text-xl sm:text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-cyan-300">
                            {office.title}
                          </h3>
                          <span className="h-1 w-8 bg-gradient-to-r from-blue-500 to-cyan-400 rounded-full" />
                        </div>
                        
                        <div className="grid md:grid-cols-2 gap-6">
                          <div>
                            <h4 className="text-sm font-semibold text-blue-300 uppercase tracking-wider mb-3 flex items-center gap-2">
                              <FaMapMarkerAlt className="text-blue-400" />
                              Address
                            </h4>
                            <ul className="space-y-3">
                              {office.address.map((item, i) => (
                                <li key={i} className="flex items-start gap-3">
                                  <span className="text-blue-400 mt-1">
                                    {typeof item === 'string' ? (
                                      <FaMapMarkerAlt />
                                    ) : (
                                      <>
                                        {item.icon === 'location' && <FaMapMarkerAlt />}
                                        {item.icon === 'road' && <FaRoad />}
                                        {item.icon === 'pin' && <FaMapPin />}
                                      </>
                                    )}
                                  </span>
                                  <span className="text-gray-300">
                                    {typeof item === 'string' ? item : item.text}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                          
                          <div>
                            <h4 className="text-sm font-semibold text-blue-300 uppercase tracking-wider mb-3 flex items-center gap-2">
                              <FaPhone className="text-blue-400" />
                              Contact
                            </h4>
                            <ul className="space-y-3">
                              {office.contact?.map((item, i) => (
                                <li key={i} className="flex items-center gap-3">
                                  <span className="text-blue-400">
                                    {item.icon === 'phone' && <FaPhone />}
                                    {item.icon === 'envelope' && <FaEnvelope />}
                                  </span>
                                  <a 
                                    href={item.icon === 'phone' ? `tel:${item.text}` : `mailto:${item.text}`}
                                    className="text-gray-300 hover:text-blue-400 transition-colors duration-200"
                                  >
                                    {item.text}
                                  </a>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Dynamic Google Map */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-12 sm:mt-16"
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-center mb-6 sm:mb-10">Find Us on Map</h2>
              <DynamicGoogleMap
                businessName="SS Solar"
                fallbackAddress="C-97, Opposite ABCD Park, Shivalik Nagar, Haridwar (U.K.) - 249403"
                className="w-full h-[400px] sm:h-[500px]"
                height="500px"
              />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Contact;